import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'

import { Button } from '@/common/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Textarea } from '@/common/ui/textarea'
import Spinner from '@/common/components/Spinner'

import useQueryGetSlideAnnouncement from '@/modules/client-setting/api/hooks/useQueryGetSlideAnnouncement'
import useMutationCreateSlideAnnouncement from '@/modules/client-setting/api/hooks/useMutationCreateSlideAnnouncement'
import useMutationUpdateSlideAnnouncement from '@/modules/client-setting/api/hooks/useMutationUpdateSlideAnnouncement'
import { SlideAnnouncementQueryKeys } from '@/modules/client-setting/keys'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'

const slideAnnouncementSchema = z.object({
	content: z.string().optional(),
})

type SlideAnnouncementFormData = z.infer<typeof slideAnnouncementSchema>

interface SlideAnnouncementFormProps {
	onContentChange: (content: string) => void
}

export default function SlideAnnouncementForm({
	onContentChange,
}: SlideAnnouncementFormProps) {
	const queryClient = useQueryClient()
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const { data: slideAnnouncement, isLoading } = useQueryGetSlideAnnouncement()

	const { mutate: createSlideAnnouncement, isPending: isCreating } =
		useMutationCreateSlideAnnouncement({
			onSuccess: () => {
				toast.success('สร้างประกาศสไลด์สำเร็จ')
				queryClient.invalidateQueries({
					queryKey: [SlideAnnouncementQueryKeys.GetSlideAnnouncement],
				})
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})

	const { mutate: updateSlideAnnouncement, isPending: isUpdating } =
		useMutationUpdateSlideAnnouncement({
			onSuccess: () => {
				toast.success('อัพเดทประกาศสไลด์สำเร็จ')
				queryClient.invalidateQueries({
					queryKey: [SlideAnnouncementQueryKeys.GetSlideAnnouncement],
				})
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})

	const form = useForm<SlideAnnouncementFormData>({
		resolver: zodResolver(slideAnnouncementSchema),
		defaultValues: {
			content: '',
		},
	})

	const watchedContent = form.watch('content')

	// Update preview when content changes
	useEffect(() => {
		onContentChange(watchedContent || '')
	}, [watchedContent, onContentChange])

	// Set form data when slide announcement is loaded
	useEffect(() => {
		if (slideAnnouncement?.content) {
			form.setValue('content', slideAnnouncement.content)
			onContentChange(slideAnnouncement.content)
		}
	}, [slideAnnouncement, form, onContentChange])

	const onSubmit = (data: SlideAnnouncementFormData) => {
		const payload = { content: data.content || '' }
		if (slideAnnouncement?.content) {
			updateSlideAnnouncement({ payload })
		} else {
			createSlideAnnouncement({ payload })
		}
	}

	const isPending = isCreating || isUpdating

	if (isLoading) {
		return <Spinner />
	}

	return (
		<Card className="w-full">
			<CardHeader>
				<CardTitle className="text-lg">
					{slideAnnouncement?.content ? 'แก้ไขประกาศสไลด์' : 'สร้างประกาศสไลด์'}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
						<FormField
							control={form.control}
							name="content"
							render={({ field }) => (
								<FormItem>
									<FormLabel>เนื้อหาประกาศ</FormLabel>
									<FormControl>
										<Textarea
											placeholder="ใส่เนื้อหาประกาศที่ต้องการแสดงบนสไลด์..."
											className="min-h-32 resize-none"
											disabled={!hasWritePermission}
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							disabled={isPending || !hasWritePermission}
							className="w-full"
						>
							{isPending && <Spinner />}
							{slideAnnouncement?.content ? 'อัพเดทประกาศ' : 'สร้างประกาศ'}
						</Button>
					</form>
				</Form>
			</CardContent>
		</Card>
	)
}
